/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
// Complex legacy form with dynamic controls - unsafe any usage is pervasive due to Angular Forms API
import { Component, OnInit, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormArray, Validators, FormGroup } from '@angular/forms';
import { Transition, StateService } from '@uirouter/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { MON_USER_PERMISSIONS, UserPermission, UserPermissionKey } from '@client/app/shared-modules/api/constants/user-permissions.constants';
import { AVAILABLE_PAGE_SIZE, DEFAULT_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { LocaleService, LocaleOptionType } from '@client/app/shared-modules/api/services/locale.service';

import { TableModule, TablePageEvent } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmationService } from 'primeng/api';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';
import { CheckboxChangeEvent } from 'primeng/checkbox';

import { ActionTriggerService } from '@client/app/shared-modules/tracking/action-trigger/action-trigger.service';
import { AdminLayoutService } from '@client/app/feature-modules/admin/services/admin-layout.service';
import { UnsavedFormCheckService } from '@client/app/shared-modules/navigation-guard/unsaved-form-check.service';
import { UserRepoService } from '@client/app/feature-modules/admin/modules/admin-users/services/user-repo.service';
import { UserFormService } from '../../services/user-form.service';
import { MergedDomain, UserData, UserFormValue } from '../../types/user.type';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';

type ApiErrorDetailMap = Record<string, string[]>;
interface ApiErrorLike {
    errors?: ApiErrorDetailMap;
    message?: string;
}
interface ApiErrorResponse {
    error?: ApiErrorLike;
    errors?: ApiErrorDetailMap;
    message?: string;
}

@Component({
    selector: 'mon-admin-user-form',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        TranslocoModule,
        TableModule,
        CardModule,
        ButtonModule,
        InputTextModule,
        CheckboxModule,
        SelectModule,
        ProgressSpinnerModule,
        MessageModule,
        ConfirmDialog,
        TooltipModule,
        PageLayoutComponent,
    ],
    providers: [
        ConfirmationService,
        UnsavedFormCheckService,
    ],
    templateUrl: './admin-user-form.component.html',
    styleUrl: './admin-user-form.component.scss',
})
export class AdminUserFormComponent implements OnInit {
    readonly AVAILABLE_PAGE_SIZE = AVAILABLE_PAGE_SIZE;
    isEditMode: boolean = false;
    loadingForm: boolean = false;
    loadingDomains: boolean = false;
    savingForm: boolean = false;
    private destroy$ = new Subject<void>();
    deregisterTransitionHook: Function | null = null;

    page: number = 1;
    pageSize: number = DEFAULT_PAGE_SIZE;
    tableFirstOffset = 0;
    perPage = 0;
    total = 0;
    languages: LocaleOptionType[] = [];
    permissions: UserPermission[] = [];

    currentPageDomains: MergedDomain[] = [];
    currentPageDomainGroupsByDomainId: Record<MergedDomain['id'], MergedDomain[]> = {};
    currentPageDomainFormControlByDomainId: Record<MergedDomain['id'], FormGroup> = {};
    currentPageDomainGroupFormControlByDomainGroupId: Record<MergedDomain['id'], FormGroup> = {};

    currentUserData: UserData | null = null;
    originalDomainPermissions: Map<number, { visible: boolean; sendReport: boolean; type: string }> = new Map();
    private initialFormValue: UserFormValue | null = null;
    // global list of all domain & domain group IDs across all pages (for header checkbox)
    private allDomainIds: number[] = [];
    allVisibleIndeterminate: boolean = false;
    allSendReportIndeterminate: boolean = false;
    private lastBulkVisibleAction: 'allChecked' | 'allUnchecked' | null = null;

    userForm: FormGroup = this.fb.group({
        firstName: ['', Validators.required],
        lastName: [''],
        email: ['', [Validators.required, Validators.email]],
        interfaceLanguage: [null],
        isAdmin: [false],
        notifyOnExport: [false],
        permissions: this.fb.group({
            allFeatures: [false],
        }),
        domains: this.fb.array([]),
        allVisible: [false],
        allSendReport: [false],
    });

    get domainsFormArray (): FormArray {
        return this.userForm.get('domains') as FormArray;
    }

    isFieldInvalid (fieldName: string): boolean {
        const field = this.userForm.get(fieldName);
        return !!(field && field.invalid && field.touched);
    }

    getFieldError (fieldName: string, errorType: string): boolean {
        const field = this.userForm.get(fieldName);
        return !!(field && field.errors?.[errorType]);
    }

    constructor (
        private readonly fb: FormBuilder,
        private transition: Transition,
        private readonly translocoService: TranslocoService,
        private readonly stateService: StateService,
        private readonly globalToastMessageService: GlobalToastMessageService,
        private readonly adminLayoutService: AdminLayoutService,
        private readonly actionTriggerService: ActionTriggerService,
        private readonly localeService: LocaleService,
        private readonly userRepoService: UserRepoService,
        private readonly unsavedFormCheckService: UnsavedFormCheckService,
        private readonly userFormService: UserFormService,
        private readonly destroyRef: DestroyRef,
    ) { }

    ngOnInit (): void {
        const id = this.transition.params().id;

        this.isEditMode = Boolean(id);
        // Defer breadcrumb setting to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => this.setBreadcrumbs(), 0);
        this.languages = this.localeService.getLocaleOptions();
        this.setupPermissions();
        this.userFormService.clearDomainSelectionCache();

        if (this.isEditMode) {
            this.loadUser(id);
        } else {
            this.userForm.addControl('sendLoginInstructions', this.fb.control(false));
            this.userForm.patchValue({ interfaceLanguage: 'en' });
            this.loadDomains();
        }

        this.setupNavigationGuard();
    }

    private setupPermissions (): void {
        const permissionTranslations: Record<UserPermissionKey, string> = {
            policies: this.translocoService.translate('admin.user.permissions.policies'),
            qa: this.translocoService.translate('admin.user.permissions.qa'),
            accessibility: this.translocoService.translate('admin.user.permissions.accessibility'),
            seo: this.translocoService.translate('admin.user.permissions.seo'),
            uptime: this.translocoService.translate('admin.user.permissions.uptime'),
            inventory: this.translocoService.translate('admin.user.permissions.inventory'),
            statistics: this.translocoService.translate('admin.user.permissions.statistics'),
            content_pages: this.translocoService.translate('admin.user.permissions.content_pages'),
            pagecorrect: this.translocoService.translate('admin.user.permissions.pagecorrect'),
            performance: this.translocoService.translate('admin.user.permissions.performance'),
            data_privacy: this.translocoService.translate('admin.user.permissions.data_privacy'),
            cookie_banner: this.translocoService.translate('admin.user.permissions.cookie_banner'),
            page_fix: this.translocoService.translate('admin.user.permissions.page_fix'),
        };

        this.permissions = Object.keys(MON_USER_PERMISSIONS)
            .filter(key => key !== 'cookie_banner' && key !== 'page_fix')
            .map(key => ({
                key: key as UserPermissionKey,
                label: permissionTranslations[key as UserPermissionKey],
            }));

        this.addPermissionControls();
    }

    private setupNavigationGuard (): void {
        this.deregisterTransitionHook = this.unsavedFormCheckService.registerGuard(
            this.transition.router.transitionService,
            () => this.hasUnsavedChanges(),
        );

        this.destroyRef.onDestroy(() => {
            if (this.deregisterTransitionHook) {
                this.deregisterTransitionHook();
            }
            this.destroy$.next();
            this.destroy$.complete();
        });
    }

    addPermissionControls () {
        const permissionsFormGroup = this.userForm.get('permissions') as FormGroup;

        this.permissions.forEach(permission => {
            permissionsFormGroup.addControl(permission.key, this.fb.control(false));
        });
    }

    async loadUser (id: string) {
        this.loadingForm = true;

        try {
            const user = await this.userRepoService.get(id);
            this.populateForm(user);
        } catch {
            this.globalToastMessageService.error(
                this.translocoService.translate('admin.user.form.error.loadMessage'),
            );
        } finally {
            this.loadingForm = false;
        }
    }

    async populateForm (user: UserData) {
        this.currentUserData = user;

        const emailNotifications = user.email_notifications as { exports?: boolean } | undefined;
        const notifyOnExport = emailNotifications?.exports || false;

        this.userForm.patchValue({
            firstName: user.first_name || '',
            lastName: user.last_name || '',
            email: user.email || '',
            interfaceLanguage: user.locale || null,
            isAdmin: Boolean(user.customer_admin) || false,
            notifyOnExport,
            allVisible: false,
            allSendReport: false,
        });

        this.setUserPermissions(user.settings?.modules ? [...user.settings.modules] : []);

        if (this.isEditMode) {
            this.userFormService.storeAllUserDomainPermissions(user);
            this.originalDomainPermissions = this.userFormService.getAllStoredPermissions();
            this.ensureAllDomainIdsLoaded().catch(() => {});
        }

        await this.loadDomains();
    }

    private setUserPermissions (userCurrentPermissions: string[]): void {
        const permissionsFormGroup = this.userForm.get('permissions') as FormGroup;
        const hasNoRestrictions = userCurrentPermissions.length === 0;

        this.permissions.forEach(permission => {
            const isAllowed = hasNoRestrictions || !userCurrentPermissions.includes(permission.key);
            permissionsFormGroup.get(permission.key)?.setValue(isAllowed);
        });

        this.updateAllFeaturesCheckbox();
    }

    private async loadDomains () {
        this.loadingDomains = true;

        try {
            const result = this.currentUserData
                ? await this.userFormService.loadUserDomains(this.currentUserData, this.page, this.pageSize)
                : await this.userFormService.loadDomainsForNewUser(this.page, this.pageSize);

            this.total = result.total;
            this.perPage = result.perPage;
            this.tableFirstOffset = ((this.page - 1) * this.pageSize);

            this.currentPageDomains = [];
            this.currentPageDomainGroupsByDomainId = {};

            let lastDomainId: number | null = null;
            for (const mergedDomainAndDomainGroup of result.domains) {
                if (mergedDomainAndDomainGroup.type === 'domain') {
                    lastDomainId = mergedDomainAndDomainGroup.id;
                    this.currentPageDomains.push(mergedDomainAndDomainGroup);
                }
                if (!lastDomainId) {
                    continue;
                }
                if (mergedDomainAndDomainGroup.type === 'domain_group') {
                    if (!this.currentPageDomainGroupsByDomainId[lastDomainId]) {
                        this.currentPageDomainGroupsByDomainId[lastDomainId] = [];
                    }
                    this.currentPageDomainGroupsByDomainId[lastDomainId].push(mergedDomainAndDomainGroup);
                }

            }

            this.setDomains(result.domains);

            if (this.userForm.get('isAdmin')?.value) {
                this.updateDomainVisibilityForAdminStatus();
            }
        } catch {
            this.globalToastMessageService.error(
                this.translocoService.translate('admin.user.form.error.loadDomainsMessage'),
            );
        } finally {
            this.loadingDomains = false;

            // capture initial form value after all async operations are complete for unsaved changes check
            setTimeout(() => {
                this.initialFormValue = JSON.parse(JSON.stringify(this.userForm.value));
            }, 100);
        }
    }

    onPageChange (pageChangeEvent: TablePageEvent) {
        this.saveDomainSelectionsToCache();

        const { first, rows } = pageChangeEvent;

        // Check if page size has changed
        const pageSizeChanged = rows !== this.perPage;

        // If page size changed, reset to page 1; otherwise calculate current page
        const pageNumber = pageSizeChanged ? 1 : Math.floor((first || 0) / (rows || 1)) + 1;

        this.page = pageNumber;
        this.pageSize = rows || this.pageSize;
        this.tableFirstOffset = first || 0;

        this.loadDomains();
    }

    private setBreadcrumbs (): void {
        this.adminLayoutService.setBreadcrumbs({
            uiStates: [
                {
                    label: this.translocoService.translate('admin.users.table.breadcrumbLabel'),
                    state: 'base.customer.admin.users',
                },
                {
                    label: this.isEditMode
                        ? this.translocoService.translate('admin.user.form.edit.breadcrumbLabel')
                        : this.translocoService.translate('admin.user.form.add.breadcrumbLabel'),
                    state: this.isEditMode ? 'base.customer.admin.edit-user' : 'base.customer.admin.add-user',
                },
            ],
        });
    }

    setDomains (domainsFromApi: MergedDomain[]) {
        const isUserAdmin = this.userForm.get('isAdmin')?.value || false;

        // populate originalDomainPermissions only if it's empty and until all permissions are loaded
        if (this.isEditMode && this.originalDomainPermissions.size === 0) {
            domainsFromApi.forEach(domain => {
                const visible = domain.visible !== undefined ? domain.visible : false;
                const sendReport = domain.sendReport !== undefined ? domain.sendReport : false;

                this.originalDomainPermissions.set(domain.id, {
                    visible,
                    // if visible = false, then sendReport = false
                    sendReport: visible ? sendReport : false,
                    type: domain.type || 'domain',
                });
            });
        }

        const domainFGs = domainsFromApi.map(domain => {
            const visible = domain.visible !== undefined ? domain.visible : false;
            const sendReport = domain.sendReport !== undefined ? domain.sendReport : false;

            const fg = this.fb.group({
                id: [domain.id],
                title: [domain.title],
                visible: [{ value: visible, disabled: isUserAdmin }],
                // if visible = false, then sendReport = false and disabled
                sendReport: [{ value: visible ? sendReport : false, disabled: !visible }],
                type: [domain.type || 'domain'],
            });

            if (domain.type === 'domain') {
                this.currentPageDomainFormControlByDomainId[domain.id] = fg;
            }

            if (domain.type === 'domain_group') {
                this.currentPageDomainGroupFormControlByDomainGroupId[domain.id] = fg;
            }

            return fg;
        });

        this.userForm.setControl('domains', this.fb.array(domainFGs));

        this.updateHeaderCheckboxes();

        this.domainsFormArray.valueChanges.subscribe(() => {
            this.userForm.markAsDirty();
            this.saveDomainSelectionsToCache();
        });
    }

    private saveDomainSelectionsToCache (): void {
        this.domainsFormArray.controls.forEach(domainControl => {
            const domainId = domainControl.get('id')?.value;
            const visible = domainControl.get('visible')?.value || false;
            const sendReport = domainControl.get('sendReport')?.value || false;

            if (domainId) {
                this.userFormService.saveDomainSelection(domainId, {
                    visible,
                    sendReport,
                });
            }
        });
    }

    toggleAllPermissions (event: CheckboxChangeEvent) {
        const permissionsFormGroup = this.userForm.get('permissions') as FormGroup;
        if (permissionsFormGroup) {
            Object.keys(permissionsFormGroup.controls).forEach(key => {
                if (key !== 'allFeatures') {
                    permissionsFormGroup.get(key)?.setValue(event.checked);
                }
            });
        }
    }

    updateAllFeaturesCheckbox () {
        const permissionsFormGroup = this.userForm.get('permissions') as FormGroup;
        if (permissionsFormGroup) {
            const allPermissionsChecked = Object.keys(permissionsFormGroup.controls)
                .filter(key => key !== 'allFeatures')
                .every(key => permissionsFormGroup.get(key)?.value === true);
            permissionsFormGroup.get('allFeatures')?.setValue(allPermissionsChecked, { emitEvent: false });
        }
    }

    onDomainVisibleChange (domainFormGroup: FormGroup, event: CheckboxChangeEvent) {
        const visible = event.checked;
        const sendReportControl = domainFormGroup.get('sendReport');

        if (!visible) {
            // if visible = false, then sendReport = false and disabled
            sendReportControl?.setValue(false);
            sendReportControl?.disable();
        } else {
            // if visible = true, then enable sendReport
            sendReportControl?.enable();
        }

        this.updateHeaderCheckboxes();
        // reset bulk flag when manual change happens
        this.lastBulkVisibleAction = null;
    }

    async onToggleAllVisible (event: CheckboxChangeEvent) {
        const checked = event.checked;

        try {
            const allIdsLoaded = await this.ensureAllDomainIdsLoaded();
            allIdsLoaded.forEach((domainId: number) => {
                const cachedConfig = this.userFormService.getCachedDomainConfig(domainId);
                this.userFormService.saveDomainSelection(domainId, {
                    visible: checked,
                    // when turning visibility off we always clear sendReport; no restoration logic
                    sendReport: checked ? (cachedConfig?.sendReport || false) : false,
                });
            });

            if (this.isEditMode && this.originalDomainPermissions.size > 0) {
                this.originalDomainPermissions.forEach((perms, id) => {
                    this.originalDomainPermissions.set(id, {
                        visible: checked,
                        sendReport: checked ? perms.sendReport : false,
                        type: perms.type,
                    });
                });
            }
        } catch {
            this.globalToastMessageService.error(
                this.translocoService.translate('admin.user.form.toast.domainIdsErrorDetail'),
            );
        }

        this.domainsFormArray.controls.forEach(ctrl => {
            const visibleControl = ctrl.get('visible');
            const sendReportControl = ctrl.get('sendReport');

            visibleControl?.setValue(checked);
            if (!checked) {
                // if unchecking visible, uncheck and disable sendReport
                sendReportControl?.setValue(false);
                sendReportControl?.disable();
            } else {
                // if checking visible, just enable sendReport (value stays whatever cache/original had)
                sendReportControl?.enable();
            }
        });

        if (this.isEditMode && this.allDomainIds.length === 0 && this.originalDomainPermissions.size > 0) {
            this.originalDomainPermissions.forEach((perms, id) => {
                this.originalDomainPermissions.set(id, {
                    visible: checked,
                    sendReport: checked ? perms.sendReport : false,
                    type: perms.type,
                });
            });
        }
        // set the bulk action before updating header checkboxes
        this.lastBulkVisibleAction = checked ? 'allChecked' : 'allUnchecked';
        this.updateHeaderCheckboxes();
    }

    async onToggleAllSendReport (event: CheckboxChangeEvent) {
        const checked = event.checked;

        try {
            const allDomainIds = await this.ensureAllDomainIdsLoaded();
            allDomainIds.forEach((domainId: number) => {
                const cachedConfig = this.userFormService.getCachedDomainConfig(domainId);
                const isVisible = cachedConfig?.visible || false;
                this.userFormService.saveDomainSelection(domainId, {
                    visible: isVisible,
                    // only update sendReport if the domain is visible
                    sendReport: isVisible ? checked : false,
                });
            });
        } catch {
            this.globalToastMessageService.error(
                this.translocoService.translate('admin.user.form.toast.domainIdsErrorDetail'),
            );
        }

        this.domainsFormArray.controls.forEach(ctrl => {
            const isVisible = ctrl.get('visible')?.value || false;
            // only update sendReport if the domain is visible
            if (isVisible) {
                ctrl.get('sendReport')?.setValue(checked);
            } else {
                ctrl.get('sendReport')?.setValue(false);
            }
        });
        this.updateHeaderCheckboxes();
    }

    async onAdminStatusChange () {
        const isUserAdmin = this.userForm.get('isAdmin')?.value || false;
        if (isUserAdmin) {
            try {
                const allDomainIds = await this.ensureAllDomainIdsLoaded();
                allDomainIds.forEach((domainId: number) => {
                    const cachedConfig = this.userFormService.getCachedDomainConfig(domainId);
                    this.userFormService.saveDomainSelection(domainId, {
                        visible: true, // all domains visible for admin
                        sendReport: cachedConfig?.sendReport || false,
                    });
                });
            } catch {
                this.globalToastMessageService.error(
                    this.translocoService.translate('admin.user.form.toast.domainIdsErrorDetail'),
                );
            }
        } else {
            if (this.isEditMode && this.originalDomainPermissions.size > 0) {
                this.userFormService.clearDomainSelectionCache();
                this.originalDomainPermissions.forEach((permissions, domainId) => {
                    this.userFormService.saveDomainSelection(domainId, {
                        visible: permissions.visible,
                        // if visible = false, then sendReport = false
                        sendReport: permissions.visible ? permissions.sendReport : false,
                    });
                });
                this.domainsFormArray.controls.forEach(domainControl => {
                    const domainId = domainControl.get('id')?.value;
                    const originalPermissions = this.originalDomainPermissions.get(domainId);
                    if (originalPermissions) {
                        const visibleControl = domainControl.get('visible');
                        const sendReportControl = domainControl.get('sendReport');

                        visibleControl?.setValue(originalPermissions.visible);

                        // if visible = false, then sendReport = false and disabled
                        if (originalPermissions.visible) {
                            sendReportControl?.setValue(originalPermissions.sendReport);
                            sendReportControl?.enable();
                        } else {
                            sendReportControl?.setValue(false);
                            sendReportControl?.disable();
                        }
                    }
                });
            }
        }
        this.updateDomainVisibilityForAdminStatus();
    }

    private updateDomainVisibilityForAdminStatus () {
        if (this.domainsFormArray.length === 0) {
            return;
        }

        const isUserAdmin = this.userForm.get('isAdmin')?.value || false;

        this.domainsFormArray.controls.forEach(domainControl => {
            const visibleControl = domainControl.get('visible');
            const sendReportControl = domainControl.get('sendReport');

            if (isUserAdmin) {
                visibleControl?.setValue(true);
                visibleControl?.disable();
                // is user is admin, enable send report since all domains are visible
                sendReportControl?.enable();
            } else {
                visibleControl?.enable();
                // update send report based on visibility
                if (visibleControl?.value) {
                    sendReportControl?.enable();
                } else {
                    sendReportControl?.setValue(false);
                    sendReportControl?.disable();
                }
            }
        });

        this.updateHeaderCheckboxes();
    }

    updateHeaderCheckboxes () {
        // use the full ID list if we have it, otherwise fall back to original permissions map
        const canFallbackToOriginal = this.isEditMode && this.originalDomainPermissions.size > 0;
        let idSource: number[] = [];
        if (this.allDomainIds.length > 0) {
            idSource = this.allDomainIds;
        } else if (canFallbackToOriginal) {
            idSource = Array.from(this.originalDomainPermissions.keys());
        }

        let allVisible: boolean;
        let anyVisible: boolean;
        let visibleCount = 0;
        let visibleWithSendReportCount = 0;

        if (idSource.length > 0) {
            const global = this.computeGlobalHeaderState(idSource);
            allVisible = global.allVisible;
            anyVisible = global.anyVisible;
            visibleCount = global.visibleCount;
            visibleWithSendReportCount = global.visibleWithSendReportCount;
        } else {
            // fallback to current page only (new user with no domains yet loaded globally)
            if (this.domainsFormArray.controls.length === 0) {
                this.userForm.get('allVisible')?.setValue(false, { emitEvent: false });
                this.userForm.get('allSendReport')?.setValue(false, { emitEvent: false });
                this.userForm.get('allSendReport')?.disable({ emitEvent: false });
                return;
            }
            allVisible = this.domainsFormArray.controls.every(ctrl => ctrl.get('visible')?.value === true);
            const visibleDomains = this.domainsFormArray.controls.filter(ctrl => ctrl.get('visible')?.value === true);
            anyVisible = visibleDomains.length > 0;
            visibleCount = visibleDomains.length;
            visibleWithSendReportCount = visibleDomains.filter(ctrl => ctrl.get('sendReport')?.value === true).length;
        }

        this.userForm.get('allVisible')?.setValue(allVisible, { emitEvent: false });
        // show the bulk Send Report checkbox as checked if:
        // 1) at least one visible domain
        // 2) all visible domains has sendReport = true
        // 3) all domains are visible
        const totalCount = this.allDomainIds.length > 0
            ? this.allDomainIds.length
            : (this.isEditMode && this.originalDomainPermissions.size > 0
                ? this.originalDomainPermissions.size
                : this.domainsFormArray.length);

        const bulkSendReportShouldBeChecked = (
            visibleCount > 0 &&
            visibleWithSendReportCount === visibleCount &&
            visibleCount === totalCount
        );

        this.userForm.get('allSendReport')?.setValue(bulkSendReportShouldBeChecked, { emitEvent: false });

        const isUserAdmin = this.userForm.get('isAdmin')?.value || false;
        if (isUserAdmin) {
            this.userForm.get('allVisible')?.disable({ emitEvent: false });
        } else {
            this.userForm.get('allVisible')?.enable({ emitEvent: false });
        }

        const allSendReportControl = this.userForm.get('allSendReport');
        if (!anyVisible) {
            allSendReportControl?.disable({ emitEvent: false });
        } else {
            allSendReportControl?.enable({ emitEvent: false });
        }
        // Re-evaluate visibleCount if the header says allVisible=false but bulk action just unchecked all
        if (!allVisible && this.lastBulkVisibleAction === 'allUnchecked') {
            visibleCount = 0;
        }
        this.allVisibleIndeterminate = visibleCount > 0 && visibleCount < totalCount;
        // Indeterminate for send report when:
        // 1) at least one visible domain has sendReport = true, AND
        // 2) Either not all visible domains have sendReport OR not all domains are visible.
        this.allSendReportIndeterminate = (
            visibleWithSendReportCount > 0 &&
            (
                visibleWithSendReportCount < visibleCount ||
                visibleCount < totalCount // all visible have sendReport but a hidden domain exists
            )
        );
    }

    hasUnsavedChanges (): boolean {
        if (!this.initialFormValue) {
            return false;
        }

        const currentValue = this.userForm.value;
        return JSON.stringify(this.initialFormValue) !== JSON.stringify(currentValue);
    }

    onSubmit () {
        this.actionTriggerService.track({
            action: 'admin-user-form-submit-btn',
        });

        if (this.userForm.invalid) {
            this.userForm.markAllAsTouched();
            this.globalToastMessageService.error(
                this.translocoService.translate('admin.user.form.invalidInputToast'),
            );
            return;
        }

        if (this.isEditMode) {
            this.updateUser();
        } else {
            this.createUser();
        }
    }

    async createUser () {
        this.savingForm = true;
        try {
            const payload = this.userFormService.createNewUserPayload(this.userForm.value);

            await this.userRepoService.create(payload);

            this.globalToastMessageService.success(
                this.translocoService.translate('admin.user.form.toast.createSuccessMessage{{firstName}}', {
                    firstName: this.userForm.value.firstName,
                }),
            );

            this.cleanupAndRedirect();
        } catch (e) {
            this.handleUserSubmissionError(e);
        } finally {
            this.savingForm = false;
        }
    }

    async updateUser () {
        this.savingForm = true;
        try {
            const payload = this.userFormService.createUserUpdatePayload(
                this.userForm.value,
                this.currentUserData || {},
            );

            await this.userRepoService.update(payload);

            this.globalToastMessageService.success(
                this.translocoService.translate('admin.user.form.toast.updateSuccessMessage{{firstName}}', {
                    firstName: this.userForm.value.firstName,
                }),
            );
            this.cleanupAndRedirect();
        } catch (e) {
            this.handleUserSubmissionError(e);
        } finally {
            this.savingForm = false;
        }
    }

    private handleUserSubmissionError (e: unknown): void {
        const normalized = e as ApiErrorResponse;
        const errorObj: ApiErrorLike | ApiErrorResponse | undefined = normalized.error || normalized;
        if (errorObj && errorObj.errors) {
            let errorDetails = '';
            for (const field of Object.keys(errorObj.errors)) {
                for (const msg of errorObj.errors[field]) {
                    if (field === 'email' && msg === 'is already in use') {
                        errorDetails += this.translocoService.translate('admin.user.form.toast.createErrorEmailInUseDetail');
                        errorDetails += '\n';
                    } else if (field === 'email' && msg === 'has already been taken') {
                        errorDetails += this.translocoService.translate('admin.user.form.toast.createErrorEmailInUseDetail');
                        errorDetails += '\n';
                    } else {
                        errorDetails += `${field} ${msg}\n`;
                    }
                }
            }
            this.globalToastMessageService.error(errorDetails, errorObj.message);
            return;
        }

        this.globalToastMessageService.error(
            this.translocoService.translate('global.error.generalErrorTitle'),
        );
    }

    // required for unsavedFormCheckService to track changes
    private cleanupAndRedirect () {
        this.userForm.markAsPristine();
        this.userForm.markAsUntouched();

        this.unsavedFormCheckService.unregisterGuard();
        this.navigateToUsersList();
    }

    async onCancel () {
        this.actionTriggerService.track({
            action: 'admin-user-form-cancel-btn',
        });

        const shouldConfirm = this.hasUnsavedChanges();
        const confirmed = await this.unsavedFormCheckService.confirmNavigation(shouldConfirm);

        if (confirmed) {
            this.userForm.markAsPristine();
            this.userForm.markAsUntouched();

            this.unsavedFormCheckService.unregisterGuard();
            this.navigateToUsersList();
        }
    }

    private navigateToUsersList (): void {
        this.stateService.go('base.customer.admin.users');
    }

    private async ensureAllDomainIdsLoaded (): Promise<number[]> {
        if (this.allDomainIds.length > 0) {
            return this.allDomainIds;
        }
        const ids = await this.userFormService.ensureAllDomainIdsLoaded();
        this.allDomainIds = ids;
        this.updateHeaderCheckboxes();
        return ids;
    }

    private computeGlobalHeaderState (idList?: number[]) {
        let allVisible = true;
        let allVisibleHaveSendReport = true;
        let anyVisible = false;
        let visibleCount = 0;
        let visibleWithSendReportCount = 0;

        const ids = idList && idList.length > 0 ? idList : this.allDomainIds;

        // check current page controls
        const currentPageMap = new Map<number, FormGroup>();
        this.domainsFormArray.controls.forEach(ctrl => {
            const id = ctrl.get('id')?.value;
            if (id !== undefined && id !== null) {
                currentPageMap.set(id, ctrl as FormGroup);
            }
        });

        for (const id of ids) {
            let visible = false;
            let sendReport = false;

            // Priority: current page form data > cached selections > original permissions > default false
            if (currentPageMap.has(id)) {
                const ctrl = currentPageMap.get(id);
                visible = ctrl?.get('visible')?.value === true;
                sendReport = ctrl?.get('sendReport')?.value === true;
            } else {
                const cached = this.userFormService.getCachedDomainConfig(id);
                if (cached) {
                    visible = cached.visible;
                    sendReport = cached.sendReport;
                } else {
                    const original = this.originalDomainPermissions.get(id);
                    if (original) {
                        visible = original.visible;
                        sendReport = original.sendReport;
                    }
                }
            }

            if (visible) {
                anyVisible = true;
                visibleCount++;
                if (sendReport) {
                    visibleWithSendReportCount++;
                } else {
                    allVisibleHaveSendReport = false;
                }
            } else {
                allVisible = false;
            }
        }

        if (!anyVisible) {
            allVisibleHaveSendReport = false;
        } else if (!allVisible) {
            allVisibleHaveSendReport = false;
        }


        return { allVisible, allVisibleHaveSendReport, anyVisible, visibleCount, visibleWithSendReportCount };
    }

}
